import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Pagination from '../../components/common/Pagination';
import AdvancedSearch, { SearchField, SearchCondition, conditionsToQueryParams, queryParamsToConditions } from '../../components/common/AdvancedSearch';

interface Attachment {
  id?: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
}

interface BoardPost {
  id: string;
  title: string;
  content: string;
  categoryId: string;
  authorName: string;
  authorDepartment: string;
  isPinned: boolean;
  isRequired: boolean;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  attachments?: Attachment[];
  scheduledAt?: string;
  status: 'published' | 'scheduled' | 'draft';
  createdAt: string;
}

interface BoardCategory {
  id: string;
  name: string;
  color: string;
}

const initialFormData = {
  title: '',
  categoryId: '',
  content: '',
  isPinned: false,
  isRequired: false,
  isScheduled: false,
  scheduledAt: '',
  relatedSurveyId: '',
};

export default function BoardList() {
  const { canCreate } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [categories, setCategories] = useState<BoardCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchConditions, setSearchConditions] = useState<SearchCondition[]>(() => {
    const saved = searchParams.get('conditions');
    return saved ? queryParamsToConditions(saved) : [];
  });
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);

  // ファイル添付
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // ページネーション・ソート・表示件数（URLから取得）
  const currentPage = parseInt(searchParams.get('page') || '1');
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const setCurrentPage = (page: number) => {
    setSearchParams(prev => { prev.set('page', String(page)); return prev; });
  };
  const setPageSize = (size: number) => {
    setSearchParams(prev => { prev.set('pageSize', String(size)); prev.delete('page'); return prev; });
  };
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: searchParams.get('sortBy') || 'createdAt',
    direction: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
  });

  // 詳細検索フィールド定義
  const searchFields: SearchField[] = useMemo(() => [
    { key: 'title', label: 'タイトル', type: 'text', placeholder: 'タイトルで検索' },
    { key: 'categoryId', label: 'カテゴリ', type: 'select', options: categories.map(c => ({ value: c.id, label: c.name })) },
    { key: 'status', label: 'ステータス', type: 'select', options: [
      { value: 'published', label: '公開済' },
      { value: 'scheduled', label: '予約中' },
      { value: 'draft', label: '下書き' },
    ]},
    { key: 'authorName', label: '投稿者', type: 'text', placeholder: '投稿者名で検索' },
    { key: 'createdAt', label: '作成日', type: 'date' },
    { key: 'isRequired', label: '必読', type: 'boolean' },
    { key: 'isPinned', label: 'ピン留め', type: 'boolean' },
  ], [categories]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [searchConditions, currentPage, pageSize, sortConfig]);

  // 検索条件をURLに保存（他パラメータを維持）
  useEffect(() => {
    setSearchParams(prev => {
      if (searchConditions.length > 0) {
        prev.set('conditions', conditionsToQueryParams(searchConditions));
      } else {
        prev.delete('conditions');
      }
      return prev;
    }, { replace: true });
  }, [searchConditions, setSearchParams]);

  const fetchCategories = async () => {
    try {
      const data = await api.get<{ items: BoardCategory[] }>('/api/board/categories');
      setCategories(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // 詳細検索条件を追加
      if (searchConditions.length > 0) {
        params.append('advancedSearch', JSON.stringify(searchConditions));
      }
      
      params.append('page', String(currentPage));
      params.append('pageSize', String(pageSize));
      params.append('sortBy', sortConfig.key);
      params.append('sortOrder', sortConfig.direction);
      
      const data = await api.get<{ items: BoardPost[]; total: number; totalPages: number }>(`/api/board/posts?${params.toString()}`);
      setPosts(Array.isArray(data?.items) ? data.items : []);
      setTotalPages(data?.totalPages ?? 1);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (conditions: SearchCondition[]) => {
    setSearchConditions(conditions);
    setSearchParams(prev => { prev.delete('page'); return prev; });
  };

  const handleSubmit = async (e: React.FormEvent, isDraft = false) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim() || submitting) return;

    setSubmitting(true);
    setUploading(true);
    try {
      // ファイルアップロード
      const uploadedAttachments: Omit<Attachment, 'id'>[] = [];
      for (const file of attachments) {
        const url = await api.uploadFile(file, { folder: 'board-attachments' });
        uploadedAttachments.push({
          fileName: file.name,
          fileUrl: url,
          fileSize: file.size,
          mimeType: file.type,
        });
      }

      const postData = {
        ...formData,
        attachments: uploadedAttachments,
        status: isDraft ? 'draft' : formData.isScheduled ? 'scheduled' : 'published',
        scheduledAt: formData.isScheduled ? formData.scheduledAt : null,
      };

      await api.post('/api/board/posts', postData);
      setShowModal(false);
      setFormData(initialFormData);
      setAttachments([]);
      fetchPosts();
    } catch (error) {
      console.error('Failed to create post:', error);
      toast.error('投稿の作成に失敗しました');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setAttachments(prev => [...prev, ...Array.from(files)]);
    }
    e.target.value = '';
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getCategoryById = (id: string) => categories.find(c => c.id === id);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Badge variant="info">予約</Badge>;
      case 'draft':
        return <Badge variant="default">下書き</Badge>;
      default:
        return null;
    }
  };

  const getMinScheduledDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);
    return now.toISOString().slice(0, 16);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">掲示板</h1>
        {canCreate('board') && (
          <Button onClick={() => setShowModal(true)} className="w-full sm:w-auto">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            投稿を作成
          </Button>
        )}
      </div>

      {/* 詳細検索 */}
      <Card>
        <AdvancedSearch
          fields={searchFields}
          onSearch={handleSearch}
          quickSearchPlaceholder="タイトルで検索..."
          quickSearchField="title"
          initialConditions={searchConditions}
        />
        
        {/* 表示件数・ソート */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t dark:border-gray-700">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
            <span className="hidden sm:inline">表示件数:</span>
            <Select
              value={pageSize.toString()}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="w-20 sm:w-24 text-sm"
            >
              <option value="10">10件</option>
              <option value="30">30件</option>
              <option value="50">50件</option>
              <option value="100">100件</option>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
            <span className="hidden sm:inline">並び替え:</span>
            <Select
              value={`${sortConfig.key}-${sortConfig.direction}`}
              onChange={(e) => {
                const [key, direction] = e.target.value.split('-');
                const next = { key, direction: direction as 'asc' | 'desc' };
                setSortConfig(next);
                setSearchParams(prev => { prev.set('sortBy', next.key); prev.set('sortOrder', next.direction); prev.delete('page'); return prev; }, { replace: true });
              }}
              className="w-32 sm:w-40 text-sm"
            >
              <option value="createdAt-desc">新しい順</option>
              <option value="createdAt-asc">古い順</option>
              <option value="viewCount-desc">閲覧数順</option>
              <option value="likeCount-desc">いいね順</option>
            </Select>
          </div>
        </div>
      </Card>

      {/* 投稿一覧 */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <Card>
            <div className="text-center py-12 text-gray-500">
              投稿がありません
            </div>
          </Card>
        ) : (
          posts.map(post => {
            const category = getCategoryById(post.categoryId);
            return (
              <Link key={post.id} to={`/board/${post.id}`}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {post.isPinned && (
                          <svg className="w-4 h-4 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                          </svg>
                        )}
                        {category && (
                          <span
                            className="px-2 py-0.5 text-xs font-medium rounded-full text-white flex-shrink-0"
                            style={{ backgroundColor: category.color }}
                          >
                            {category.name}
                          </span>
                        )}
                        {post.isRequired && <Badge variant="danger">必読</Badge>}
                        {getStatusBadge(post.status)}
                        {post.scheduledAt && post.status === 'scheduled' && (
                          <span className="text-xs text-gray-500">
                            {new Date(post.scheduledAt).toLocaleString('ja-JP', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}公開予定
                          </span>
                        )}
                      </div>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-1">
                        {post.title}
                      </h3>
                      <p className="text-gray-500 text-sm line-clamp-2 mb-3">
                        {post.content}
                      </p>

                      {/* 添付ファイル表示 */}
                      {post.attachments && post.attachments.length > 0 && (
                        <div className="flex gap-2 mb-3 flex-wrap">
                          {post.attachments.slice(0, 3).map((att, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs text-gray-600 dark:text-gray-300">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              {att.fileName.length > 15 ? att.fileName.slice(0, 12) + '...' : att.fileName}
                            </span>
                          ))}
                          {post.attachments.length > 3 && (
                            <span className="text-xs text-gray-500">+{post.attachments.length - 3}</span>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 flex-wrap">
                        <span>{post.authorName}</span>
                        {post.authorDepartment && <span className="hidden sm:inline">・{post.authorDepartment}</span>}
                        <span>・{new Date(post.createdAt).toLocaleDateString('ja-JP')}</span>
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          {post.viewCount}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                          {post.likeCount}
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          {post.commentCount || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })
        )}
      </div>

      {/* ページネーション */}
      <div className="flex justify-center">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setFormData(initialFormData); setAttachments([]); }} title="投稿を作成" size="lg">
        <form onSubmit={(e) => handleSubmit(e)} className="space-y-4">
          <Input
            label="タイトル"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">カテゴリ</label>
            <Select
              value={formData.categoryId}
              onChange={(e) => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
            >
              <option value="">選択してください</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              本文 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 text-gray-900 dark:text-white"
              rows={8}
              required
            />
          </div>

          {/* ファイル添付 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">添付ファイル</label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
              multiple
            />
            <Button type="button" variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              ファイルを選択
            </Button>
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {attachments.map((file, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                    <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                    <button type="button" onClick={() => handleRemoveAttachment(index)} className="text-red-500 hover:text-red-700">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 予約投稿 */}
          <div className="p-4 border dark:border-gray-700 rounded-lg">
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={formData.isScheduled}
                onChange={(e) => setFormData(prev => ({ ...prev, isScheduled: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">予約投稿</span>
            </label>
            {formData.isScheduled && (
              <Input
                label="公開日時"
                type="datetime-local"
                value={formData.scheduledAt}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduledAt: e.target.value }))}
                min={getMinScheduledDateTime()}
                required={formData.isScheduled}
              />
            )}
          </div>

          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isPinned}
                onChange={(e) => setFormData(prev => ({ ...prev, isPinned: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">ピン留め</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isRequired}
                onChange={(e) => setFormData(prev => ({ ...prev, isRequired: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">必読</span>
            </label>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => { setShowModal(false); setFormData(initialFormData); setAttachments([]); }} className="w-full sm:w-auto">
              キャンセル
            </Button>
            <Button type="button" variant="secondary" onClick={(e) => handleSubmit(e, true)} disabled={submitting || uploading} className="w-full sm:w-auto">
              下書き保存
            </Button>
            <Button type="submit" disabled={submitting || uploading} className="w-full sm:w-auto">
              {submitting || uploading ? '投稿中...' : formData.isScheduled ? '予約投稿' : '投稿'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

